import { createTheme } from "@vanilla-extract/css";
import { hslaWithAlpha } from "../utils/hslaWithAlpha";
import { primitiveTokens, semanticThemeTokens } from "./ThemeTokens.css";

const backgroundGradient = {
  1: primitiveTokens.colors.gradients.light[1],
  2: primitiveTokens.colors.gradients.light[2],
  3: primitiveTokens.colors.gradients.light[3],
  4: primitiveTokens.colors.gradients.light[4]
};

const chart = {
  borderColour: "transparent",
  backgroundColor: "transparent",
  textColor: primitiveTokens.colors.neutral[900],
  tooltipBackground: primitiveTokens.colors.white[1000],
  tooltipTextColor: primitiveTokens.colors.neutral[900],
  foregroundColor: "rgba(0, 0, 0, 1)",
  chromeBackgroundColor: primitiveTokens.colors.neutral[50],
  chromeSubtleTextColor: primitiveTokens.colors.neutral[600],
  crosshairLabelBackgroundColor: primitiveTokens.colors.neutral[300],
  crosshairLabelTextColor: primitiveTokens.colors.neutral[900],
  gridColor: primitiveTokens.colors.neutral[200],
  palette: {
    fill: {
      1: primitiveTokens.colors.red[500],
      2: primitiveTokens.colors.purple[400],
      3: primitiveTokens.colors.red[300],
      4: primitiveTokens.colors.blue[300],
      5: primitiveTokens.colors.purple[300],
      6: primitiveTokens.colors.red[300],
      7: primitiveTokens.colors.neutral[600],
      8: primitiveTokens.colors.red[700],
      9: primitiveTokens.colors.purple[600],
      10: primitiveTokens.colors.red[700]
    },
    stroke: {
      1: primitiveTokens.colors.neutral[800],
      2: primitiveTokens.colors.purple[600],
      3: primitiveTokens.colors.red[700],
      4: primitiveTokens.colors.blue[800],
      5: primitiveTokens.colors.purple[700],
      6: primitiveTokens.colors.red[600],
      7: primitiveTokens.colors.neutral[800],
      8: primitiveTokens.colors.blue[900],
      9: primitiveTokens.colors.purple[800],
      10: primitiveTokens.colors.red[900]
    }
  }
};

const button = {
  primary: {
    disabled: {
      backgroundColor: primitiveTokens.colors.neutral[400],
      textColor: primitiveTokens.colors.white[1000]
    },
    focus: { backgroundColor: primitiveTokens.colors.purple[600] },
    hover: { backgroundColor: primitiveTokens.colors.purple[500] },
    default: { backgroundColor: primitiveTokens.colors.purple[600] },
    active: { backgroundColor: primitiveTokens.colors.purple[400] },
    textColor: primitiveTokens.colors.white[1000]
  },
  secondary: {
    default: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.white[100], 0.92),
      borderColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.36)
    },
    disabled: {
      backgroundColor: primitiveTokens.colors.white[1000],
      textColor: primitiveTokens.colors.neutral[400],
      borderColor: primitiveTokens.colors.neutral[200]
    },
    focus: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.48),
      boxShadow: primitiveTokens.colors.shadow.focusRing
    },
    hover: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.64)
    },

    active: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.8)
    },
    loading: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.8)
    },
    textColor: primitiveTokens.colors.purple[700]
  },
  tertiary: {
    disabled: {
      textColor: primitiveTokens.colors.neutral[500]
    },
    hover: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.24)
    },
    active: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.8)
    },
    loading: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.8)
    },
    textColor: primitiveTokens.colors.purple[700]
  }
};

const checkbox = {
  labelTextColor: primitiveTokens.colors.purple[700],
  unchecked: {
    backgroundColor: primitiveTokens.colors.purple[50],
    default: {
      borderColor: primitiveTokens.colors.purple[300]
    },
    focus: {
      borderColor: primitiveTokens.colors.purple[300],
      boxShadow: primitiveTokens.colors.shadow.focusRing
    },
    hover: {
      backgroundColor: primitiveTokens.colors.purple[50],
      borderColor: primitiveTokens.colors.purple[400]
    },
    active: {
      backgroundColor: primitiveTokens.colors.purple[100],
      borderColor: primitiveTokens.colors.purple[400]
    },
    disabled: {
      labelTextColor: primitiveTokens.colors.neutral[400],
      backgroundColor: primitiveTokens.colors.neutral[50],
      borderColor: primitiveTokens.colors.neutral[200]
    }
  },
  checked: {
    checkmarkColor: primitiveTokens.colors.white[1000],
    borderColor: "transparent",
    backgroundColor: primitiveTokens.colors.purple[300],
    focus: {
      borderColor: "transparent",
      boxShadow: primitiveTokens.colors.shadow.focusRing
    },
    hover: {
      borderColor: primitiveTokens.colors.purple[400]
    },
    active: {
      backgroundColor: primitiveTokens.colors.purple[500],
      borderColor: "transparent"
    },
    disabled: {
      backgroundColor: primitiveTokens.colors.neutral[200],
      borderColor: primitiveTokens.colors.neutral[200],
      checkmarkColor: primitiveTokens.colors.neutral[500],
      labelTextColor: primitiveTokens.colors.neutral[400]
    }
  }
};

const chip = {
  backgroundColor: primitiveTokens.colors.purple[50],
  textColor: primitiveTokens.colors.purple[600],
  borderColor: primitiveTokens.colors.purple[100],
  closeIconColor: primitiveTokens.colors.purple[600],
  hoverBorderColor: primitiveTokens.colors.purple[200],
  disabledBackgroundColor: primitiveTokens.colors.neutral[50],
  disabledTextColor: primitiveTokens.colors.neutral[500],
  disabledBorderColor: primitiveTokens.colors.neutral[200],
  disabledCloseIconColor: primitiveTokens.colors.neutral[500]
};

const color = {
  textColor: primitiveTokens.colors.purple[700],
  backgroundColor: {
    default: primitiveTokens.colors.white[1000]
  }
};

const dataTable = {
  primaryColor: primitiveTokens.colors.purple[600],
  colorScheme: "light",
  backgroundColor: primitiveTokens.colors.white[1000],
  headerBackgroundColor: hslaWithAlpha(
    primitiveTokens.colors.purple[700],
    0.02
  ),
  borderColor: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.16),
  row: {
    colorPalette: {
      1: primitiveTokens.colors.white[1000],
      2: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.3),
      3: hslaWithAlpha(primitiveTokens.colors.purple[50], 0.6),
      4: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.35),
      5: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.45),
      6: hslaWithAlpha(primitiveTokens.colors.purple[50], 0.65),
      7: hslaWithAlpha(primitiveTokens.colors.purple[50], 0.7),
      8: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.55),
      9: hslaWithAlpha(primitiveTokens.colors.purple[50], 0.8),
      10: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.6)
    },
    crossTableRowStyle: {
      match: primitiveTokens.colors.green[200],
      shift: primitiveTokens.colors.yellow[200]
    },
    borderColor: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.16),
    backgroundColorOdd: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.04),
    selected: {
      backgroundColor: primitiveTokens.colors.purple[200]
    },
    hover: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.06)
    }
  },
  dropdown: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.neutral[50], 0.94)
  },
  cell: {
    positive: primitiveTokens.colors.green[600],
    negative: primitiveTokens.colors.red[600],
    warning: primitiveTokens.colors.yellow[600],
    highlight: primitiveTokens.colors.blue[600]
  }
};
const dialog = {
  overlay: {
    backgroundColor: "hsla(253, 2%, 16%, 0.48)"
  },
  content: {
    background: primitiveTokens.colors.white[1000],
    boxShadow: primitiveTokens.colors.shadow.popover,
    textColor: primitiveTokens.colors.purple[900]
  },
  title: {
    fontSize: primitiveTokens.type.md,
    textColor: primitiveTokens.colors.neutral[900]
  },
  description: {
    textColor: primitiveTokens.colors.neutral[600],
    fontSize: primitiveTokens.type.sm
  },
  iconButton: {
    backgroundColor: primitiveTokens.colors.neutral[100],
    textColor: primitiveTokens.colors.neutral[700]
  }
};

const toast = {
  default: {
    backgroundColor: primitiveTokens.colors.neutral[50],
    borderColor: primitiveTokens.colors.neutral[300],
    titleColor: primitiveTokens.colors.neutral[900],
    descriptionColor: primitiveTokens.colors.neutral[700]
  },
  success: {
    backgroundColor: primitiveTokens.colors.green[50],
    borderColor: primitiveTokens.colors.green[300],
    titleColor: primitiveTokens.colors.neutral[900],
    descriptionColor: primitiveTokens.colors.neutral[700]
  },
  warning: {
    backgroundColor: primitiveTokens.colors.yellow[50],
    borderColor: primitiveTokens.colors.yellow[300],
    titleColor: primitiveTokens.colors.neutral[900],
    descriptionColor: primitiveTokens.colors.neutral[700]
  },
  error: {
    backgroundColor: primitiveTokens.colors.red[50],
    borderColor: primitiveTokens.colors.red[300],
    titleColor: primitiveTokens.colors.neutral[900],
    descriptionColor: primitiveTokens.colors.neutral[700]
  }
};

const divider = {
  backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.16),
  color: hslaWithAlpha(primitiveTokens.colors.purple[900], 0.48)
};

// Opaque: the page must not show through.
const drawer = {
  backgroundColor: primitiveTokens.colors.purple[50]
};

const dropdown = {
  trigger: {
    default: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.48),
      color: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.8)
    },
    hover: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.64),
      color: primitiveTokens.colors.purple[700]
    },
    active: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.8),
      color: primitiveTokens.colors.purple[700]
    }
  },
  content: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.8),
    color: primitiveTokens.colors.purple[800]
  },
  item: {
    default: {
      backgroundColor: primitiveTokens.colors.white[100]
    },
    hover: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.48),
      color: primitiveTokens.colors.purple[700]
    }
  }
};

const input = {
  backgroundColor: primitiveTokens.colors.white[1000],
  cursorColor: primitiveTokens.colors.purple[300],
  filled: {
    active: {
      borderColor: primitiveTokens.colors.purple[300]
    },
    disabled: {
      backgroundColor: primitiveTokens.colors.neutral[50],
      borderColor: primitiveTokens.colors.neutral[200],
      textColor: primitiveTokens.colors.neutral[600]
    },
    hover: {
      borderColor: primitiveTokens.colors.purple[300]
    },
    default: {
      borderColor: primitiveTokens.colors.purple[200]
    },

    textColor: primitiveTokens.colors.purple[900]
  },
  invalid: {
    backgroundColor: primitiveTokens.colors.red[50],
    borderColor: primitiveTokens.colors.red[500]
  },
  unfilled: {
    active: {
      borderColor: primitiveTokens.colors.purple[200]
    },
    disabled: {
      backgroundColor: primitiveTokens.colors.neutral[50],
      borderColor: primitiveTokens.colors.neutral[200],
      textColor: primitiveTokens.colors.neutral[500]
    },
    hover: {
      borderColor: primitiveTokens.colors.purple[300]
    },
    default: {
      borderColor: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.16)
    },
    textColor: primitiveTokens.colors.neutral[500]
  }
};

const label = {
  color: {
    disabled: primitiveTokens.colors.neutral[400],
    optionalDisabled: primitiveTokens.colors.neutral[400],
    optionalRegular: primitiveTokens.colors.neutral[600],
    regular: primitiveTokens.colors.neutral[600]
  }
};

const loginPage = {
  background: primitiveTokens.colors.gradients.light[5],
  heading: {
    color: primitiveTokens.colors.purple[800]
  },
  container: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.8),
    borderColor: primitiveTokens.colors.white[1000]
  },
  forgotPassword: {
    color: primitiveTokens.colors.purple[700]
  },
  copyRight: {
    color: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.48)
  }
};

const logo = { fill: primitiveTokens.colors.purple[800] };

const tile = {
  backgroundColor: primitiveTokens.colors.white[1000],
  borderColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.36)
};

const navigationMenu = {
  fontSize: primitiveTokens.type.sm,
  placeholderColor: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.8),
  selected: {
    backgroundColor: primitiveTokens.colors.purple[100],
    color: primitiveTokens.colors.purple[800]
  }
};

const radio = {
  backgroundColor: primitiveTokens.colors.purple[50],
  borderColor: primitiveTokens.colors.purple[300],
  hover: {
    borderColor: primitiveTokens.colors.purple[400]
  },
  focus: {
    boxShadow: primitiveTokens.colors.shadow.focusRing
  },
  active: {
    backgroundColor: primitiveTokens.colors.purple[100],
    borderColor: primitiveTokens.colors.purple[400]
  },
  disabled: {
    backgroundColor: primitiveTokens.colors.neutral[50],
    borderColor: primitiveTokens.colors.neutral[200]
  },
  checkmark: {
    backgroundColor: primitiveTokens.colors.white[1000],
    borderColor: primitiveTokens.colors.purple[400],
    hover: {
      borderColor: primitiveTokens.colors.purple[300]
    },
    active: {
      borderColor: primitiveTokens.colors.purple[500]
    },
    disabled: {
      backgroundColor: primitiveTokens.colors.neutral[500],
      borderColor: primitiveTokens.colors.neutral[200]
    }
  },
  label: {
    textColor: primitiveTokens.colors.purple[700],
    invalid: {
      textColor: primitiveTokens.colors.red[500]
    },
    disabled: {
      textColor: primitiveTokens.colors.neutral[400]
    }
  }
};

const select = {
  default: {
    textColor: primitiveTokens.colors.neutral[600],
    textColorSelected: primitiveTokens.colors.purple[700],
    backgroundColor: primitiveTokens.colors.white[1000],
    borderColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.36)
  },
  focus: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.64)
  },
  hover: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.64)
  },
  disabled: {
    backgroundColor: primitiveTokens.colors.neutral[50],
    borderColor: primitiveTokens.colors.neutral[200],
    textColor: primitiveTokens.colors.neutral[400],
    textColorSelected: primitiveTokens.colors.neutral[500]
  },
  menuList: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.48),
    listItem: {
      textColor: primitiveTokens.colors.purple[700],
      hover: {
        backgroundColor: primitiveTokens.colors.purple[50]
      },
      selected: {
        backgroundColor: primitiveTokens.colors.purple[100]
      }
    }
  },

  icon: {
    color: primitiveTokens.colors.purple[600],
    disabledColor: primitiveTokens.colors.neutral[500]
  }
};

const sheet = {
  transitionDuration: primitiveTokens.transitionDuration.default,
  shadow: primitiveTokens.colors.shadow.sheet,
  layer1: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.48)
  },
  layer2: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.64)
  },
  layer3: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.8)
  }
};

const switchStyle = {
  label: {
    textColor: primitiveTokens.colors.purple[700],
    textcolorDisabled: primitiveTokens.colors.neutral[500]
  },
  toggle: {
    indicatorColor: primitiveTokens.colors.white[1000],
    indicatorColorDisabled: primitiveTokens.colors.neutral[500]
  },
  backgroundColor: {
    default: {
      toggled: primitiveTokens.colors.purple[300],
      untoggled: primitiveTokens.colors.neutral[400]
    },
    hover: {
      toggled: primitiveTokens.colors.purple[400],
      untoggled: primitiveTokens.colors.neutral[500]
    },
    disabled: {
      toggled: primitiveTokens.colors.neutral[200],
      untoggled: primitiveTokens.colors.neutral[200]
    }
  },
  thumb: {
    backgroundColor: primitiveTokens.colors.white[1000]
  }
};

const tabs = {
  trigger: {
    backgroundColor: "transparent",
    borderColor: "transparent",
    transitionDuration: primitiveTokens.transitionDuration.default,
    transitionProperty: "background-color, color, border-color",
    textColor: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.64),
    hover: {
      textColor: primitiveTokens.colors.neutral[700],
      backgroundColor: primitiveTokens.colors.neutral[100]
    },
    selected: {
      borderColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.36),
      backgroundColor: primitiveTokens.colors.white[1000],
      textColor: primitiveTokens.colors.purple[700]
    },
    disabled: {
      textColor: primitiveTokens.colors.neutral[400],
      borderColor: primitiveTokens.colors.neutral[200],
      backgroundColor: primitiveTokens.colors.neutral[50],
      selected: {
        textColor: primitiveTokens.colors.neutral[500],
        backgroundColor: primitiveTokens.colors.neutral[50]
      }
    },
    focus: {
      boxShadow: primitiveTokens.colors.shadow.focusRing,
      outline: "none"
    }
  },
  separator: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.04)
  },
  list: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.12),
    color: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.24)
  }
};

const toggleGroup = {
  item: {
    borderColor: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.24),
    hover: { backgroundColor: primitiveTokens.colors.neutral[100] },
    selected: {
      backgroundColor: primitiveTokens.colors.purple[100],
      borderColor: primitiveTokens.colors.purple[300],
      textColor: primitiveTokens.colors.purple[700]
    },
    focus: {
      boxShadow: primitiveTokens.colors.shadow.focusRing,
      outline: "none"
    }
  }
};

const themeSwitcher = {
  backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.16),
  textColor: primitiveTokens.colors.purple[800],
  iconColor: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.64),
  thumbColor: primitiveTokens.colors.white[1000]
};

const transition = primitiveTokens.animations.elementFade;

const userMenu = {
  iconColor: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.64)
};

const sparklineGraph = {
  trend: {
    stroke: primitiveTokens.colors.blue[500]
  },
  seasonality: {
    stroke: primitiveTokens.colors.green[400]
  },
  styleSeries: {
    color: primitiveTokens.colors.graph.neutral[700],
    fill: hslaWithAlpha(primitiveTokens.colors.graph.neutral[700], 0.45)
  },
  rosFwd16: {
    negativeMarker: primitiveTokens.colors.red[500]
  },
  rebuyOverstock: {
    full: primitiveTokens.colors.red[500],
    partial: primitiveTokens.colors.graph.yellow[400]
  },
  // Stepped off the purple ramp rather than taken from it wholesale: the
  // primitive steps are tuned for surfaces and wobble in lightness, which
  // collapses adjacent stages into each other on a 13px bar.
  orderFlow: {
    1: "hsla(253, 100%, 80%, 1)",
    2: "hsla(253, 80%, 70%, 1)",
    3: "hsla(253, 55%, 60%, 1)",
    4: "hsla(253, 42%, 49%, 1)",
    5: "hsla(253, 40%, 37%, 1)",
    6: "hsla(253, 40%, 25%, 1)"
  }
};

const tabsNav = {
  fontSize: primitiveTokens.type.sm,
  default: {
    backgroundColor: "transparent",
    textColor: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.64)
  },
  active: {
    backgroundColor: primitiveTokens.colors.white[1000],
    textColor: primitiveTokens.colors.purple[700]
  },
  hover: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.48),
    textColor: primitiveTokens.colors.purple[700]
  }
};

const barChart = {
  salesFill: primitiveTokens.colors.graph.blue[300],
  purchaseFill: primitiveTokens.colors.graph.yellow[500],
  sellthroughFill: primitiveTokens.colors.graph.green[500]
};

const sellthroughChart = {
  color1: primitiveTokens.colors.graph.blue[300],
  color2: primitiveTokens.colors.graph.blue[400],
  color3: primitiveTokens.colors.graph.purple[400],
  color4: primitiveTokens.colors.graph.yellow[400],
  color5: primitiveTokens.colors.graph.red[400]
};

const transferMap = {
  shape: {
    fill: primitiveTokens.colors.purple[700],
    stroke: primitiveTokens.colors.purple[100]
  }
};

const avatar = {
  color: primitiveTokens.colors.purple[600],
  backgroundColor: primitiveTokens.colors.purple[100]
};

const note = {
  info: {
    backgroundColor: primitiveTokens.colors.purple[50],
    textColor: primitiveTokens.colors.purple[900]
  },
  warning: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.yellow[500], 0.12),
    textColor: primitiveTokens.colors.yellow[700]
  },
  error: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.red[500], 0.12),
    textColor: primitiveTokens.colors.red[700]
  }
};
const errorDetails = {
  container: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.red[50], 0.8),
    borderColor: hslaWithAlpha(primitiveTokens.colors.red[300], 0.4)
  },
  summary: {
    textColor: primitiveTokens.colors.red[700]
  },
  stack: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.red[100], 0.6),
    textColor: primitiveTokens.colors.red[800]
  }
};

const groupCard = {
  headerOverlayColor: primitiveTokens.colors.neutral[100],
  statBorderColor: primitiveTokens.colors.neutral[100],
  statLabelTextColor: primitiveTokens.colors.neutral[600],
  thumbnailBorderColor: primitiveTokens.colors.neutral[300]
};

export const lightTheme = createTheme(semanticThemeTokens, {
  backgroundGradient,
  button,
  checkbox,
  chip,
  note,
  barChart,
  sellthroughChart,
  color,
  dataTable,
  divider,
  drawer,
  dropdown,
  input,
  label,
  loginPage,
  logo,
  tile,
  navigationMenu,
  radio,
  select,
  sheet,
  switch: switchStyle,
  tabs,
  toggleGroup,
  themeSwitcher,
  transition,
  userMenu,
  dialog,
  toast,
  tabsNav,
  sparklineGraph,
  chart,
  transferMap,
  avatar,
  errorDetails,
  groupCard
});
